import cloneDeep from "../../../../utilities/cloneDeep";
import { generateGuid } from "../../../../utilities/generateGuid";
import {
  ChartReportConfigurationForRunning,
  ChartStyleSettings,
  ConditionField,
  DimensionDescriptor,
  FieldConfiguration,
  PivotReportConfigurationForRunning,
  ReportField,
  ReportType,
  TabularReportConfigurationForRunning,
  TileReportConfigurationForRunning,
} from "../../../api/biClient.types";
import {
  createPivotDefaultSettings,
  createTabularDefaultSettings,
  createTileDefaultSettings,
} from "../../../utils/isConfigurationValid";
import {
  initializeCommonRunState,
  isChartRunState,
  isPivotRunState,
  isTabularRunState,
  isTileRunState,
} from "./runStateUtils";

export type RunStateBase = {
  //triggers a refresh of the grid
  refreshToken: string;
  dimensionNames: string[];
  //meta dimensions
  dimensions: DimensionDescriptor[];
  measureNames: string[];
  fieldsWithCustomConditionParameters: ReportField[];
};

export type ReportConfigurationForRunning =
  | TabularReportConfigurationForRunning
  | PivotReportConfigurationForRunning
  | ChartReportConfigurationForRunning
  | TileReportConfigurationForRunning;

export type TabularRunState = TabularReportConfigurationForRunning & RunStateBase;
export type PivotRunState = PivotReportConfigurationForRunning & RunStateBase;
export type ChartRunState = ChartReportConfigurationForRunning & RunStateBase;
export type TileRunState = TileReportConfigurationForRunning & RunStateBase;

export type RunState = TabularRunState | PivotRunState | ChartRunState | TileRunState;

export type RunStateAction =
  | {
      type: "Initialize";
      payload: {
        response: ReportConfigurationForRunning;
        reportId: string;
      };
    }
  | {
      type: "UpdateFieldConfig";
      payload: {
        field: ReportField;
        changes: Partial<FieldConfiguration>;
      };
    }
  | {
      type: "UpdateCondition";
      payload: {
        field: ConditionField;
        changes: Partial<ConditionField>;
      };
    }
  | {
      type: "RefreshGrid";
    };

export const reducer = (state: RunState, action: RunStateAction): RunState => {
  switch (action.type) {
    case "Initialize": {
      if (isTabularRunState(action.payload.response)) {
        const { response } = action.payload;
        return initializeTabularRunState(response, action.payload.reportId);
      }
      if (isPivotRunState(action.payload.response)) {
        const { response } = action.payload;
        return initializePivotRunState(response, action.payload.reportId);
      }
      if (isChartRunState(action.payload.response)) {
        const { response } = action.payload;
        return initializeChartRunState(response, action.payload.reportId);
      }
      if (isTileRunState(action.payload.response)) {
        const { response } = action.payload;
        return initializeTileRunState(response, action.payload.reportId);
      }
      throw new Error("Invalid report type");
    }
    case "UpdateCondition": {
      const { field, changes } = action.payload;
      return {
        ...state,
        conditions: updateConditionItem(field, changes, state.conditions),
      };
    }
    case "UpdateFieldConfig": {
      if (isTileRunState(state)) {
        const stateCopy = cloneDeep(state);
        const updatedFields = updateFieldConfig(stateCopy.fields, action);
        const updatedFieldsWithCustomConditionParameters = updateFieldConfig(
          stateCopy.fieldsWithCustomConditionParameters,
          action
        );
        return {
          ...stateCopy,
          fields: updatedFields,
          fieldsWithCustomConditionParameters: updatedFieldsWithCustomConditionParameters,
        };
      }
      if (isTabularRunState(state)) {
        const stateCopy = cloneDeep(state);
        const updatedFields = updateFieldConfig(stateCopy.fields, action);
        const updatedFieldsWithCustomConditionParameters = updateFieldConfig(
          stateCopy.fieldsWithCustomConditionParameters,
          action
        );
        return {
          ...stateCopy,
          fields: updatedFields,
          fieldsWithCustomConditionParameters: updatedFieldsWithCustomConditionParameters,
        };
      }
      if (isPivotRunState(state) || isChartRunState(state)) {
        const stateCopy = cloneDeep(state);
        const updatedFields = updateFieldConfig(stateCopy.values, action);
        const updatedFieldsWithCustomConditionParameters = updateFieldConfig(
          stateCopy.fieldsWithCustomConditionParameters,
          action
        );
        return {
          ...stateCopy,
          fields: updatedFields,
          fieldsWithCustomConditionParameters: updatedFieldsWithCustomConditionParameters,
        };
      }
      throw new Error("Invalid report type");
    }
    case "RefreshGrid": {
      return {
        ...state,
        refreshToken: generateGuid(),
      };
    }
    default:
      throw new Error("Invalid action type");
  }
};

export const initializeTileRunState = (response: TileReportConfigurationForRunning, reportId: string) => {
  return {
    ...initializeCommonRunState({ type: "Initialize", payload: { response, reportId } }),
    settings: response.settings,
    fields: response.fields,
  };
};

export const initializeTabularRunState = (response: TabularReportConfigurationForRunning, reportId: string) => {
  return {
    ...initializeCommonRunState({ type: "Initialize", payload: { response, reportId } }),
    settings: response.settings,
    fields: response.fields,
    grouping: response.grouping,
    sort: response.sort,
  };
};

export const initializePivotRunState = (response: PivotReportConfigurationForRunning, reportId: string) => {
  return {
    ...initializeCommonRunState({ type: "Initialize", payload: { response, reportId } }),
    settings: response.settings,
    rows: response.rows,
    columns: response.columns,
    values: response.values,
    sort: response.sort,
  };
};

export const initializeChartRunState = (response: ChartReportConfigurationForRunning, reportId: string) => {
  return {
    ...initializeCommonRunState({ type: "Initialize", payload: { response, reportId } }),
    settings: response.settings,
    arguments: response.arguments,
    values: response.values,
    sort: response.sort,
  };
};

const updateFieldConfig = (fields: ReportField[], action: Extract<RunStateAction, { type: "UpdateFieldConfig" }>) => {
  const field = fields.find((f) => f.config.guid === action.payload.field.config.guid);
  if (field !== undefined) {
    Object.assign(field.config, action.payload.changes);
  }
  return fields;
};

const updateConditionItem = (
  item: ConditionField,
  changedValues: Partial<ConditionField>,
  fields: ConditionField[]
) => {
  const copy = cloneDeep(fields);
  const field = copy.find((condition) => condition.config.guid === item.config.guid);
  if (field !== undefined) {
    Object.assign(field, changedValues);
    return copy;
  }
  return copy;
};

export const getInitialTabularState = (): TabularRunState => {
  return {
    dataSetId: "",
    dimensions: [],
    reportType: ReportType.Tabular,
    settings: createTabularDefaultSettings(),
    fields: [],
    grouping: [],
    conditions: [],
    sort: [],
    showParameters: false,
    fieldsWithCustomConditionParameters: [],
    refreshToken: "",
    dimensionNames: [],
    measureNames: [],
    books: [],
    isMultiBook: false,
    fieldIdsWithCustomConditionParameters: [],
    customConditionsMeta: [],
  };
};

export const getInitialPivotState = (): PivotRunState => {
  return {
    dataSetId: "",
    dimensions: [],
    reportType: ReportType.Pivot,
    showParameters: false,
    fieldsWithCustomConditionParameters: [],
    refreshToken: "",
    dimensionNames: [],
    measureNames: [],
    books: [],
    isMultiBook: false,
    conditions: [],
    rows: [],
    columns: [],
    values: [],
    sort: [],
    settings: createPivotDefaultSettings(),
    fieldIdsWithCustomConditionParameters: [],
    customConditionsMeta: [],
  };
};

export const getInitialChartState = (reportType: ReportType): ChartRunState => {
  return {
    dataSetId: "",
    dimensions: [],
    reportType: reportType,
    arguments: [],
    values: [],
    conditions: [],
    sort: [],
    showParameters: false,
    fieldsWithCustomConditionParameters: [],
    refreshToken: "",
    dimensionNames: [],
    measureNames: [],
    books: [],
    isMultiBook: false,
    settings: {
      type: reportType,
    } as ChartStyleSettings,
    fieldIdsWithCustomConditionParameters: [],
    customConditionsMeta: [],
  };
};

export const getInitialTileState = (): TileRunState => {
  return {
    dataSetId: "",
    dimensions: [],
    reportType: ReportType.Tile,
    settings: createTileDefaultSettings(),
    fields: [],
    conditions: [],
    showParameters: false,
    fieldsWithCustomConditionParameters: [],
    refreshToken: "",
    dimensionNames: [],
    measureNames: [],
    books: [],
    isMultiBook: false,
    fieldIdsWithCustomConditionParameters: [],
    customConditionsMeta: [],
  };
};
